const X_NAME_PATTERNS = ['x', 'step', 'steps', 'epoch', 'epochs', 'iteration', 'iterations', 'iter', 'time', 'time_step', 'global_step'];
const IDENTIFIER_NAME_PATTERNS = ['model', 'method', 'variant', 'dataset', 'task', 'name', 'label', 'group', 'category', 'series', 'split', 'condition'];
const NUMERIC_IDENTIFIER_PATTERNS = ['year', 'date', 'period', 'quarter', 'month', 'week', 'day', 'id', 'index', 'run'];
const VALUE_NAME_PATTERNS = ['value', 'score', 'metric', 'loss', 'accuracy', 'acc', 'f1', 'auc', 'latency', 'throughput', 'precision', 'recall', 'avg', 'mean'];
const SERIES_NAME_PATTERNS = ['series', 'group', 'split', 'condition', 'variant', 'method', 'model'];

function normalizeName(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function hasNamePattern(name, patterns) {
  const normalized = normalizeName(name);
  return patterns.some((pattern) => normalized === pattern || normalized.includes(pattern));
}

function computeColumnStats(rows, columnName) {
  const values = rows.map((row) => row[columnName]).filter((value) => value !== null && value !== undefined && String(value).trim() !== '');
  const numericValues = values
    .map((value) => Number(value))
    .filter((value) => !Number.isNaN(value));
  const stringValues = values.map((value) => String(value));

  let monotonicAscending = true;
  for (let index = 1; index < numericValues.length; index += 1) {
    if (numericValues[index] < numericValues[index - 1]) {
      monotonicAscending = false;
      break;
    }
  }

  return {
    nonEmptyCount: values.length,
    uniqueCount: new Set(stringValues).size,
    numericCount: numericValues.length,
    monotonicAscending: numericValues.length >= 2 ? monotonicAscending : false,
  };
}

function getIdentifierCandidate(columns, statsByName) {
  const stringColumns = columns.filter((column) => column.type === 'string');
  if (stringColumns.length === 0) return null;

  const ranked = [...stringColumns].sort((left, right) => {
    const leftStats = statsByName[left.name];
    const rightStats = statsByName[right.name];
    const leftScore =
      (hasNamePattern(left.name, IDENTIFIER_NAME_PATTERNS) ? 4 : 0) +
      (leftStats.uniqueCount >= Math.max(2, Math.floor(leftStats.nonEmptyCount * 0.5)) ? 2 : 0);
    const rightScore =
      (hasNamePattern(right.name, IDENTIFIER_NAME_PATTERNS) ? 4 : 0) +
      (rightStats.uniqueCount >= Math.max(2, Math.floor(rightStats.nonEmptyCount * 0.5)) ? 2 : 0);
    return rightScore - leftScore;
  });

  return ranked[0];
}

function getSeriesCandidate(columns) {
  return columns
    .filter((column) => column.type === 'string')
    .sort((left, right) => {
      const leftScore = hasNamePattern(left.name, SERIES_NAME_PATTERNS) ? 1 : 0;
      const rightScore = hasNamePattern(right.name, SERIES_NAME_PATTERNS) ? 1 : 0;
      return rightScore - leftScore;
    })[0] || null;
}

function getLikelyXColumn(columns, statsByName) {
  const numericColumns = columns.filter((column) => column.type === 'number');
  if (numericColumns.length === 0) return null;

  const ranked = [...numericColumns].sort((left, right) => {
    const leftStats = statsByName[left.name];
    const rightStats = statsByName[right.name];
    const leftScore =
      (hasNamePattern(left.name, X_NAME_PATTERNS) ? 5 : 0) +
      (leftStats.monotonicAscending ? 3 : 0) +
      (leftStats.uniqueCount >= Math.max(2, Math.floor(leftStats.nonEmptyCount * 0.6)) ? 1 : 0) -
      (hasNamePattern(left.name, VALUE_NAME_PATTERNS) ? 2 : 0);
    const rightScore =
      (hasNamePattern(right.name, X_NAME_PATTERNS) ? 5 : 0) +
      (rightStats.monotonicAscending ? 3 : 0) +
      (rightStats.uniqueCount >= Math.max(2, Math.floor(rightStats.nonEmptyCount * 0.6)) ? 1 : 0) -
      (hasNamePattern(right.name, VALUE_NAME_PATTERNS) ? 2 : 0);
    return rightScore - leftScore;
  });

  return ranked[0];
}

function buildLineWideInference(columns, statsByName) {
  const xColumn = getLikelyXColumn(columns, statsByName);
  if (!xColumn) return null;

  const valueColumns = columns
    .filter((column) => column.type === 'number' && column.name !== xColumn.name)
    .map((column) => column.name);

  if (valueColumns.length < 2) return null;

  const confidence =
    hasNamePattern(xColumn.name, X_NAME_PATTERNS) || statsByName[xColumn.name].monotonicAscending
      ? 'high'
      : 'medium';

  return {
    shape: 'line-wide',
    confidence,
    suggestedMapping: {
      inputShape: 'wide',
      x: xColumn.name,
      yColumns: valueColumns,
    },
    notes: `We'll use "${xColumn.name}" as the x-axis and plot the other ${valueColumns.length} columns as separate lines. Click "Adjust columns" if you'd like to pick different ones.`,
  };
}

function buildLineLongInference(columns, statsByName) {
  const xColumn = getLikelyXColumn(columns, statsByName);
  if (!xColumn) return null;

  const numericColumns = columns.filter((column) => column.type === 'number' && column.name !== xColumn.name);
  if (numericColumns.length !== 1) return null;

  const seriesColumn = getSeriesCandidate(columns.filter((column) => column.name !== xColumn.name));
  const valueColumn = numericColumns[0];

  return {
    shape: 'line-long',
    confidence: seriesColumn || hasNamePattern(xColumn.name, X_NAME_PATTERNS) ? 'high' : 'medium',
    suggestedMapping: {
      inputShape: 'long',
      x: xColumn.name,
      y: valueColumn.name,
      ...(seriesColumn ? { series: seriesColumn.name } : {}),
    },
    notes: seriesColumn
      ? `Plotting "${valueColumn.name}" over "${xColumn.name}", split into separate lines by "${seriesColumn.name}". Use "Adjust columns" to change.`
      : `Plotting "${valueColumn.name}" over "${xColumn.name}". Use "Adjust columns" below to swap axes or change the mapping.`,
  };
}

function getNumericIdentifierFallback(columns, statsByName) {
  const numericColumns = columns.filter((column) => column.type === 'number');
  if (numericColumns.length < 3) return null;

  const byPattern = numericColumns.find((col) => hasNamePattern(col.name, NUMERIC_IDENTIFIER_PATTERNS));
  if (byPattern) return byPattern;

  const candidates = numericColumns.filter((col) => {
    const stats = statsByName[col.name];
    return stats.monotonicAscending && stats.uniqueCount === stats.nonEmptyCount;
  });
  return candidates.length === 1 ? candidates[0] : null;
}

function buildCategoryWideInference(columns, chartId, statsByName) {
  let identifierColumn = getIdentifierCandidate(columns, statsByName);
  const numericColumns = columns.filter((column) => column.type === 'number');

  if (!identifierColumn && numericColumns.length >= 3) {
    identifierColumn = getNumericIdentifierFallback(columns, statsByName);
  }

  if (!identifierColumn || numericColumns.length < 2) return null;

  const valueNumericColumns = numericColumns.filter((column) => column.name !== identifierColumn.name);

  if (chartId === 'radar') {
    return {
      shape: 'category-wide',
      confidence: 'high',
      suggestedMapping: {
        inputShape: 'wide',
        series: identifierColumn.name,
        axisColumns: valueNumericColumns.map((column) => column.name),
      },
      notes: `"${identifierColumn.name}" labels each series. Each numeric column becomes a radar axis — remove any you don't want via "Adjust columns."`,
    };
  }

  if (['grouped-bar', 'horizontal-grouped-bar', 'stacked-bar', 'horizontal-stacked-bar'].includes(chartId)) {
    const valueColumns = valueNumericColumns.map((column) => column.name);
    if (valueColumns.length === 0) return null;
    const isNumericId = identifierColumn.type === 'number';
    return {
      shape: 'category-wide',
      confidence: isNumericId ? 'medium' : 'high',
      suggestedMapping: {
        inputShape: 'wide',
        category: identifierColumn.name,
        valueColumns,
      },
      notes: `"${identifierColumn.name}" labels each group, with ${valueColumns.length} metrics as ${chartId.includes('grouped') ? 'side-by-side bars' : 'stacked segments'}. Use "Adjust columns" to change which metrics to include.`,
    };
  }

  return {
    shape: 'category-wide',
    confidence: 'medium',
    suggestedMapping: null,
    notes: `Your table has one label column and several numeric columns. Use "Adjust columns" to choose which ones to plot.`,
  };
}

const COLORBY_CHART_IDS = new Set(['vertical-bar', 'horizontal-bar', 'lollipop', 'diverging-bar']);

function buildCategoryLongInference(columns, statsByName, chartId) {
  const stringColumns = columns.filter((column) => column.type === 'string');
  const numericColumns = columns.filter((column) => column.type === 'number');
  if (stringColumns.length === 0 || numericColumns.length !== 1) return null;

  // Charts that use colorBy instead of series for the extra string column
  const useColorBy = COLORBY_CHART_IDS.has(chartId);

  let categoryColumn;
  let seriesColumn;

  if (useColorBy && stringColumns.length >= 2) {
    // For colorBy charts: the column with MORE unique values → category (bar labels)
    // the column with FEWER unique values → colorBy (color groups)
    const sorted = [...stringColumns].sort(
      (a, b) => statsByName[b.name].uniqueCount - statsByName[a.name].uniqueCount
    );
    categoryColumn = sorted[0];
    seriesColumn = sorted[1];
  } else {
    categoryColumn = getIdentifierCandidate(stringColumns, statsByName) || stringColumns[0];
    seriesColumn = stringColumns.find((column) => column.name !== categoryColumn.name) || null;
  }

  if ((['grouped-bar', 'horizontal-grouped-bar', 'stacked-bar', 'horizontal-stacked-bar'].includes(chartId)) && !seriesColumn) return null;
  if (chartId === 'radar') return null;

  const extraKey = useColorBy ? 'colorBy' : 'series';

  return {
    shape: 'category-long',
    confidence: seriesColumn ? 'high' : 'medium',
    suggestedMapping: {
      inputShape: 'long',
      category: categoryColumn ? categoryColumn.name : stringColumns[0].name,
      value: numericColumns[0].name,
      ...(seriesColumn ? { [extraKey]: seriesColumn.name } : {}),
    },
    notes: seriesColumn
      ? `"${categoryColumn ? categoryColumn.name : stringColumns[0].name}" is the category, "${numericColumns[0].name}" is the value, and "${seriesColumn.name}" ${useColorBy ? 'colors the data' : 'groups the data'}. Use "Adjust columns" to swap.`
      : `"${categoryColumn ? categoryColumn.name : stringColumns[0].name}" is the category and "${numericColumns[0].name}" is the value. Use "Adjust columns" if this isn't right.`,
  };
}

function buildSingleMetricInference(columns) {
  const numericColumns = columns.filter((column) => column.type === 'number');
  if (numericColumns.length !== 1) return null;

  return {
    shape: 'single-metric',
    confidence: 'medium',
    suggestedMapping: {
      value: numericColumns[0].name,
    },
    notes: `Using "${numericColumns[0].name}" as the value column. If your CSV has extra columns, go back and remove them with the ▾ column menu.`,
  };
}

function buildHeatmapWideInference(columns, statsByName) {
  const stringColumns = columns.filter((column) => column.type === 'string');
  const numericColumns = columns.filter((column) => column.type === 'number');

  if (stringColumns.length !== 1 || numericColumns.length < 2) return null;

  const rowLabelCol = stringColumns[0];
  const valueColNames = numericColumns.map((c) => c.name);
  const rowCount = statsByName[rowLabelCol.name].nonEmptyCount;

  if (numericColumns.length < 2 || rowCount < 2) return null;

  return {
    shape: 'heatmap-wide',
    confidence: 'high',
    suggestedMapping: {
      inputShape: 'wide',
      rowLabelColumn: rowLabelCol.name,
      valueColumns: valueColNames,
    },
    notes: `Detected a ${rowCount}×${valueColNames.length} matrix. We'll auto-pivot this into heatmap format — "${rowLabelCol.name}" labels the rows and the numeric columns become the column axis.`,
  };
}

function buildHeatmapInference(columns, statsByName) {
  const stringColumns = columns.filter((column) => column.type === 'string');
  const numericColumns = columns.filter((column) => column.type === 'number');

  const wideResult = buildHeatmapWideInference(columns, statsByName);
  if (wideResult) return wideResult;

  if (stringColumns.length < 2 || numericColumns.length !== 1) return null;

  const sortedStrings = [...stringColumns].sort((left, right) => {
    const leftStats = statsByName[left.name];
    const rightStats = statsByName[right.name];
    const leftScore =
      (hasNamePattern(left.name, ['x', 'column', 'col', 'predicted', 'model', 'method']) ? 3 : 0) +
      (leftStats.uniqueCount >= 2 ? 1 : 0);
    const rightScore =
      (hasNamePattern(right.name, ['x', 'column', 'col', 'predicted', 'model', 'method']) ? 3 : 0) +
      (rightStats.uniqueCount >= 2 ? 1 : 0);
    return rightScore - leftScore;
  });

  const xColumn = sortedStrings[0];
  const yColumn = sortedStrings.find((column) => column.name !== xColumn.name) || sortedStrings[1];
  const valueColumn = numericColumns[0];

  return {
    shape: 'heatmap-long',
    confidence: hasNamePattern(valueColumn.name, VALUE_NAME_PATTERNS) ? 'high' : 'medium',
    suggestedMapping: {
      x: xColumn.name,
      y: yColumn.name,
      value: valueColumn.name,
    },
    notes: `"${xColumn.name}" → columns, "${yColumn.name}" → rows, "${valueColumn.name}" → cell values. Use "Adjust columns" if the axes need swapping.`,
  };
}

function buildScatterInference(columns, statsByName) {
  const numericColumns = columns.filter((column) => column.type === 'number');
  const stringColumns = columns.filter((column) => column.type === 'string');
  if (numericColumns.length < 2) return null;

  const X_SCATTER_PATTERNS = ['x', 'latency', 'cost', 'param', 'size', 'time', 'input', 'inference'];
  const Y_SCATTER_PATTERNS = ['y', 'accuracy', 'acc', 'score', 'benchmark', 'f1', 'performance', 'quality', 'loss'];
  const CATEGORY_SCATTER_PATTERNS = ['category', 'class', 'cluster', 'family', 'group', 'type', 'model', 'label'];

  const rankedX = [...numericColumns].sort((a, b) => {
    const aScore = (hasNamePattern(a.name, X_SCATTER_PATTERNS) ? 3 : 0) +
      (statsByName[a.name].monotonicAscending ? 1 : 0);
    const bScore = (hasNamePattern(b.name, X_SCATTER_PATTERNS) ? 3 : 0) +
      (statsByName[b.name].monotonicAscending ? 1 : 0);
    return bScore - aScore;
  });
  const xCol = rankedX[0];

  const remainingNumeric = numericColumns.filter((c) => c.name !== xCol.name);
  const rankedY = [...remainingNumeric].sort((a, b) => {
    const aScore = hasNamePattern(a.name, Y_SCATTER_PATTERNS) ? 3 : 0;
    const bScore = hasNamePattern(b.name, Y_SCATTER_PATTERNS) ? 3 : 0;
    return bScore - aScore;
  });
  const yCol = rankedY[0];

  const categoryCol = stringColumns.length > 0
    ? ([...stringColumns].sort((a, b) => {
        const aScore = hasNamePattern(a.name, CATEGORY_SCATTER_PATTERNS) ? 3 : 0;
        const bScore = hasNamePattern(b.name, CATEGORY_SCATTER_PATTERNS) ? 3 : 0;
        return bScore - aScore;
      })[0])
    : null;

  const sizeCol = remainingNumeric.find((c) => c.name !== yCol.name && hasNamePattern(c.name, ['size', 'weight', 'count', 'magnitude'])) || null;

  return {
    shape: 'scatter',
    confidence: 'high',
    suggestedMapping: {
      x: xCol.name,
      y: yCol.name,
      ...(categoryCol ? { category: categoryCol.name } : {}),
      ...(sizeCol ? { size: sizeCol.name } : {}),
    },
    notes: categoryCol
      ? `Plotting "${yCol.name}" vs "${xCol.name}", color-coded by "${categoryCol.name}". Use "Adjust columns" to pick different axes.`
      : `Plotting "${yCol.name}" vs "${xCol.name}". Use "Adjust columns" to swap axes or add a color-coding column.`,
  };
}

function buildComboInference(columns, statsByName) {
  const numericColumns = columns.filter((column) => column.type === 'number');
  if (numericColumns.length < 2) return null;

  let categoryCol = getIdentifierCandidate(columns, statsByName);
  if (!categoryCol && numericColumns.length >= 3) {
    categoryCol = getNumericIdentifierFallback(columns, statsByName);
  }
  if (!categoryCol) return null;

  const valueNumerics = numericColumns.filter((c) => c.name !== categoryCol.name);
  if (valueNumerics.length < 2) return null;

  const BAR_PATTERNS = ['bar', 'count', 'total', 'volume', 'revenue', 'throughput', 'accuracy', 'score'];
  const LINE_PATTERNS = ['line', 'rate', 'pct', 'percent', 'ratio', 'error', 'latency', 'cost'];

  const ranked = [...valueNumerics].sort((a, b) => {
    const aBarScore = hasNamePattern(a.name, BAR_PATTERNS) ? 2 : 0;
    const bBarScore = hasNamePattern(b.name, BAR_PATTERNS) ? 2 : 0;
    return bBarScore - aBarScore;
  });

  const barCol = ranked[0];
  const lineCol = ranked.find((c) => c.name !== barCol.name) || valueNumerics[1];

  const barHasPattern = hasNamePattern(barCol.name, BAR_PATTERNS);
  const lineHasPattern = hasNamePattern(lineCol.name, LINE_PATTERNS);

  return {
    shape: 'combo',
    confidence: barHasPattern || lineHasPattern ? 'high' : 'medium',
    suggestedMapping: {
      category: categoryCol.name,
      barValue: barCol.name,
      lineValue: lineCol.name,
    },
    notes: `"${barCol.name}" will be bars (left axis) and "${lineCol.name}" will be the line (right axis), labeled by "${categoryCol.name}". Use "Adjust columns" to swap them.`,
  };
}

function inferByChart(chartId, columns, statsByName) {
  if (chartId === 'line-chart') {
    return buildLineWideInference(columns, statsByName) || buildLineLongInference(columns, statsByName);
  }

  if (chartId === 'heatmap') {
    return buildHeatmapInference(columns, statsByName);
  }

  if (['grouped-bar', 'horizontal-grouped-bar', 'stacked-bar', 'horizontal-stacked-bar', 'radar'].includes(chartId)) {
    return buildCategoryWideInference(columns, chartId, statsByName) || buildCategoryLongInference(columns, statsByName, chartId);
  }

  if (chartId === 'scatter-plot') {
    return buildScatterInference(columns, statsByName);
  }

  if (chartId === 'combo') {
    return buildComboInference(columns, statsByName);
  }

  return null;
}

export function inferTableStructure(rows, columns, chartId) {
  if (!rows?.length || !columns?.length) {
    return {
      shape: 'ambiguous',
      confidence: 'low',
      suggestedMapping: null,
      notes: 'Add some data first — go back and paste, upload, or type values into the table.',
    };
  }

  const statsByName = Object.fromEntries(columns.map((column) => [column.name, computeColumnStats(rows, column.name)]));

  return (
    inferByChart(chartId, columns, statsByName) ||
    buildCategoryWideInference(columns, chartId, statsByName) ||
    buildLineWideInference(columns, statsByName) ||
    buildLineLongInference(columns, statsByName) ||
    buildCategoryLongInference(columns, statsByName, chartId) ||
    buildSingleMetricInference(columns) || {
      shape: 'ambiguous',
      confidence: 'low',
      suggestedMapping: null,
      notes: 'We couldn\'t auto-detect the layout — use "Adjust columns" below to tell us which column is which.',
    }
  );
}

export function getSuggestedMapping(rows, columns, chartId) {
  return inferTableStructure(rows, columns, chartId).suggestedMapping;
}

/**
 * Detect duplicate key combinations in the data for a given chart's mapping.
 * Returns { hasDuplicates, duplicateGroups: [{ key, count, example }], totalDuplicateRows }
 */
export function detectDuplicates(rows, mapping, chartId) {
  if (!rows?.length || !mapping) return { hasDuplicates: false, duplicateGroups: [], totalDuplicateRows: 0 };

  let keyColumns = [];
  if (['grouped-bar', 'horizontal-grouped-bar', 'stacked-bar', 'horizontal-stacked-bar'].includes(chartId)) {
    if (mapping.inputShape === 'wide') return { hasDuplicates: false, duplicateGroups: [], totalDuplicateRows: 0 };
    keyColumns = [mapping.category, mapping.series].filter(Boolean);
  } else if (['vertical-bar', 'horizontal-bar', 'lollipop', 'diverging-bar'].includes(chartId)) {
    keyColumns = [mapping.category].filter(Boolean);
  } else if (chartId === 'heatmap' && mapping.inputShape !== 'wide') {
    keyColumns = [mapping.x, mapping.y].filter(Boolean);
  } else if (chartId === 'combo') {
    keyColumns = [mapping.category].filter(Boolean);
  }

  if (keyColumns.length === 0) return { hasDuplicates: false, duplicateGroups: [], totalDuplicateRows: 0 };

  const counts = new Map();
  rows.forEach((row) => {
    const key = keyColumns.map((col) => String(row[col] ?? '')).join(' \u2192 ');
    counts.set(key, (counts.get(key) || 0) + 1);
  });

  const duplicateGroups = [];
  let totalDuplicateRows = 0;
  for (const [key, count] of counts) {
    if (count > 1) {
      duplicateGroups.push({ key, count });
      totalDuplicateRows += count;
    }
  }

  return { hasDuplicates: duplicateGroups.length > 0, duplicateGroups, totalDuplicateRows };
}
